<template>
  <Loading v-if="!dynamicForm" />
  <div v-else>
    <popup-duplicate-dynamic-form ref="popupDuplicateDynamicForm" />
    <vx-card no-shadow>
      <div class="vx-row mb-2 mt-2">
        <div class="vx-col sm:w-2/5 w-full">
          <h2>{{ dynamicForm.title }}</h2>
        </div>
      </div>

      <div class="vx-col sm:w-3/5 w-full mb-8">
        <p class="mb-0">
          A dynamic form can be shown on Rekoms Umbraco websites. This is the
          dynamic form template. The logo, background-, text-, button
          background- and button text-color will automatically be adjusted to
          match the individual design of a venue.
          <br />
        </p>
        <p class="b-5">
          You can specify dynamic content by using the <b>Merge Tags</b> feature
          in the form editor.<br />
        </p>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Active</span>
        </div>
        <div class="vx-col">
          <vs-switch color="success" v-model="dynamicForm.enabled" />
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Form ID</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <code>{{ dynamicForm.id }}</code>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Title</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-input label="" v-model="dynamicForm.title" class="w-full" />
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Category</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-input label="" v-model="dynamicForm.category" class="w-full" />
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Label(s)</span>
          <br />
          <small>Added to guests after they submit the form</small>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-select class="w-full" multiple v-model="dynamicForm.labels">
            <vs-select-item
              :key="item"
              :value="item"
              :text="item"
              v-for="item in labels"
            />
          </vs-select>
        </div>
      </div>

      <vs-divider />

      <h4 class="mb-2">Automatic popup settings</h4>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Open automatically</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-checkbox v-model="dynamicForm.isAutomatic" />
        </div>
      </div>

      <div v-if="dynamicForm.isAutomatic" class="pb-4">
        <div class="vx-row mb-4">
          <div class="vx-col sm:w-1/5 w-full">
            <span>Delay</span>
            <br />
            <small>Seconds before the popup will show</small>
          </div>
          <div class="vx-col sm:w-2/5 w-full">
            <span>{{ dynamicForm.autoInfo.delay }} seconds</span>
            <vs-slider max="120" v-model="dynamicForm.autoInfo.delay" />
          </div>
        </div>

        <div class="vx-row mb-6">
          <div class="vx-col sm:w-1/5 w-full">
            <span>Venues</span>
            <br />
            <small>Will only show on selected venues</small>
          </div>
          <div class="vx-col sm:w-2/5 w-full">
            <venue-selector
              v-model="dynamicForm.autoInfo.venueIds"
              :multiple="true"
            />
          </div>
        </div>

        <!-- <div class="vx-row mb-6">
          <div class="vx-col sm:w-1/5 w-full">
            <span>Excluded domain paths</span>
            <br />
            <small
              >Will not show the popup if the domain path starts with any of the specified values<br />Example:
              <i>/checkout</i></small
            >
          </div>
          <div class="vx-col sm:w-2/5 w-full">
            <vs-list>
              <vs-list-item
                v-for="domain in dynamicForm.autoInfo.excludedDomainPaths"
                :key="domain"
                :title="domain"
              >
                <feather-icon
                  icon="TrashIcon"
                  class="cursor-pointer mr-2"
                  svgClasses="h-5 w-5 stroke-2"
                  @click="deleteDomain(domain)"
                />
              </vs-list-item>
            </vs-list>
            <div class="flex">
              <vs-input class="mr-2" v-model="inputDomain"></vs-input>
              <vs-button @click="addDomain" type="border">Add</vs-button>
            </div>
          </div>
        </div> -->

        <div class="vx-row mb-6" v-if="dynamicForm.isAutomatic">
          <div class="vx-col sm:w-1/5 w-full">
            <span>Time constrained</span>
            <br />
            <small>Only show in a given period of time</small>
          </div>
          <div class="vx-col">
            <vs-switch
              color="success"
              v-model="dynamicForm.autoInfo.isTimeConstrained"
            />
          </div>
        </div>

        <div class="vx-row mb-6" v-if="dynamicForm.autoInfo.isTimeConstrained">
          <div class="vx-col sm:w-1/5 w-full">
            <span>Time period</span>
          </div>

          <div class="vx-col sm:w-2/5 max-w-sm">
            <span
              ><b>Start date:</b>
              {{ dynamicForm.autoInfo.startDate | formatDate }}</span
            >
            <DatePicker
              mode="dateTime"
              :attributes="calendarAttributes"
              is24hr
              is-expanded
              v-model="dynamicForm.autoInfo.startDate"
            ></DatePicker>
          </div>
          <div class="vx-col sm:w-2/5 max-w-sm">
            <span
              ><b>End date:</b>
              {{ dynamicForm.autoInfo.endDate | formatDate }}</span
            >
            <DatePicker
              mode="dateTime"
              :attributes="calendarAttributes"
              is24hr
              is-expanded
              v-model="dynamicForm.autoInfo.endDate"
            ></DatePicker>
          </div>
        </div>
      </div>

      <!-- <div class="vx-col sm:w-2/5">
        <vs-row vs-justify="flex-end">
          <vx-card class="max-w-sm">
            <b>Implementation code</b><br />
            <span>Select how the form should be loaded</span>
            <vs-row class="mt-3" vs-justify="center">
              <vs-button
                @click="
                  codePopupText = previewCodes.button;
                  codePopupActive = true;
                "
                class="w-full mb-2"
                >Form button</vs-button
              >
              <vs-button
                @click="
                  codePopupText = previewCodes.link;
                  codePopupActive = true;
                "
                class="w-full mb-2"
                >Form link</vs-button
              >
              <vs-button
                @click="
                  codePopupText = previewCodes.inline;
                  codePopupActive = true;
                "
                class="w-full"
                >Inline form</vs-button
              >
            </vs-row>
          </vx-card>
        </vs-row>
      </div> -->

      <Loading v-if="isLoading" />
      <vs-row v-else>
        <vs-col>
          <form-editor
            :legal-documents="legalDocuments"
            :interests="interests"
            :formJson="dynamicForm.formJson"
            ref="formEditor"
          />
        </vs-col>
        <vs-row class="top-editor-wrapper py-2 px-4" vs-align="center">
          <vs-button @click="previewForVenue" class="px-4" type="border"
            >Preview</vs-button
          >
          <span class="mx-2">for venue</span>

          <vs-select v-model="previewVenue">
            <vs-select-item
              :key="item.value"
              :value="item.value"
              :text="item.text"
              v-for="item in selectablePreviewVenues"
            />
          </vs-select>
        </vs-row>
      </vs-row>

      <!-- Save & Remove Button -->
      <vs-row
        vs-justify="space-between"
        class="flex flex-wrap items-center"
        style="margin-top: 32px"
        ><div>
          <vs-button
            v-if="hasPermission('vp.crm.dynamicforms.edit')"
            id="remove-button"
            @click="confirmRemove"
            color="danger"
            class="mt-2"
            >Remove</vs-button
          >
          <vs-button
            id="warning-button"
            @click="$refs.popupDuplicateDynamicForm.show()"
            v-if="hasPermission('vp.crm.dynamicforms.edit')"
            color="warning"
            class="mt-2 ml-3"
            >Duplicate</vs-button
          >
        </div>
        <div>
          <vs-button
            :disabled="!isConfiguredCorrectly"
            v-if="hasPermission('vp.crm.dynamicforms.edit')"
            id="save-button"
            @click="update"
            class="mt-2 mr-2"
            >Save Changes</vs-button
          >
          <vs-button
            :disabled="!isConfiguredCorrectly"
            v-if="hasPermission('vp.crm.dynamicforms.edit')"
            id="push-button"
            @click="confirmExport"
            class="mt-2"
            >Push to websites</vs-button
          >
        </div>
      </vs-row>
    </vx-card>

    <vs-popup
      v-if="codePopupActive"
      :active.sync="codePopupActive"
      title="Embed code"
    >
      <p class="pb-2">Copy and paste the following code to embed the form</p>
      <vs-textarea readonly v-model="codePopupText"></vs-textarea>
    </vs-popup>

    <vs-popup
      fullscreen
      :active.sync="previewPopupActive"
      title="Preview"
      id="previewPopup"
    >
      <iframe
        v-if="previewPopupActive"
        width="100%"
        height="95%"
        style="border: none"
        :srcdoc="iframeCode"
      ></iframe>
    </vs-popup>
  </div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import { DatePicker } from "v-calendar";
import FormEditor from "@/components/FormEditor/FormEditor.vue";
import VenueSelector from "@/components/VenueSelector.vue";
import PopupDuplicateDynamicForm from "./PopupDuplicateDynamicForm.vue";

export default {
  components: {
    DatePicker,
    FormEditor,
    VenueSelector,
    PopupDuplicateDynamicForm,
  },
  props: {},
  data() {
    return {
      inputDomain: "",

      codePopupActive: false,
      codePopupText: "",

      previewVenue: "",
      previewHtml: "",
      previewPopupActive: false,

      isLoading: true,
      calendarAttributes: [
        {
          key: "today",
          highlight: "gray",
          dates: new Date(),
        },
      ],
      dynamicFormId: "",
    };
  },
  methods: {
    addDomain() {
      this.dynamicForm.autoInfo.excludedDomainPaths.push(this.inputDomain);
      this.inputDomain = "";
    },
    deleteDomain(domainToDelete) {
      this.dynamicForm.autoInfo.excludedDomainPaths =
        this.dynamicForm.autoInfo.excludedDomainPaths.filter(
          (domain) => domain !== domainToDelete
        );
    },
    async update() {
      this.$vs.loading();

      try {
        const formExport = await this.$refs.formEditor.export();
        this.dynamicForm.formJson = JSON.stringify(formExport.design);
        // this.dynamicForm.formHtml = formExport.html;

        await this.$store.dispatch("dynamicForms/update", this.dynamicForm);

        this.$vs.notify({
          color: "green",
          title: "Dynamic form saved",
          text: "The Dynamic form was successfully saved",
        });
      } catch (err) {
        console.error(err);

        this.$vs.notify({
          color: "danger",
          title: "Dynamic form not saved",
          text: "Dynamic form could not be saved",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    async confirmRemove() {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: `Confirm`,
        text: "Are you sure you want to delete this dynamic form?",
        accept: this.remove,
      });
    },
    async remove() {
      this.$vs.loading();
      await this.$store.dispatch("dynamicForms/delete", this.dynamicFormId);
      this.$vs.loading.close();
      this.$router.go(-1);
    },
    async confirmExport() {
      this.$vs.dialog({
        type: "confirm",
        color: "primary",
        title: `Confirm`,
        text: "Are you sure you want to push this form to the live websites? It will be immediately visible to visitors.",
        accept: async () => {
          this.$vs.loading({ text: "Pushing form to websites..." });

          try {
            const formExport = await this.$refs.formEditor.export();
            this.dynamicForm.formJson = JSON.stringify(formExport.design);

            // Also saves
            await this.$store.dispatch(
              "dynamicForms/exportDynamicForm",
              this.dynamicForm
            );

            this.$vs.notify({
              color: "green",
              title: "Dynamic form saved",
              text: "The Dynamic form was successfully exported to all websites",
            });
          } catch (err) {
            console.error(err);

            this.$vs.notify({
              color: "danger",
              title: "Dynamic form not saved",
              text: "Dynamic form could not be exported",
            });
          } finally {
            this.$vs.loading.close();
          }
        },
      });
    },
    async previewForVenue() {
      if (this.previewVenue == "") return;
      this.$vs.loading({ text: "Loading preview..." });
      try {
        const formExport = await this.$refs.formEditor.export();
        this.previewHtml = await this.$store.dispatch(
          "dynamicForms/previewForVenue",
          {
            venueId: this.previewVenue,
            formJson: JSON.stringify(formExport.design),
          }
        );
        this.previewPopupActive = true;
      } finally {
        this.$vs.loading.close();
      }
    },
  },
  computed: {
    ...mapGetters({
      venues: "venues/venues",
      dynamicForm: "dynamicForms/dynamicForm",
      legalDocuments: "legalDocuments/documentsEnabled",
      interests: "guestSegmentation/interests",
      labels: "guestSegmentation/labels",
    }),

    isConfiguredCorrectly() {
      if (!this.dynamicForm.title) return false;
      if (this.dynamicForm.isAutomatic) {
        if (this.dynamicForm.autoInfo.isTimeConstrained) {
          if (!this.dynamicForm.autoInfo.startDate) return false;
          if (!this.dynamicForm.autoInfo.endDate) return false;
          // If start date is after end date, return false
          if (
            this.dynamicForm.autoInfo.startDate >
            this.dynamicForm.autoInfo.endDate
          )
            return false;
        }
      }
      return true;
    },

    iframeCode() {
      console.log(this.previewVenue, this.dynamicFormId);
      return `
        <html>
        <head>
          <${"script"} type="text/javascript" src="https://rekomlifestorageprod.blob.core.windows.net/crm/rekom-form.js"></${"script"}>
        </head>
        <body style="display:flex; justify-content: center;">
          <rekom-form-manager venueid="${
            this.previewVenue
          }" preview-mode="true"></rekom-form-manager>
          <div rekom-inline-form>Form</div> 
          <${"script"}>
          const $formManager = document.querySelector("rekom-form-manager");
          $formManager.getVueInstance().setPreviewHtml(${JSON.stringify(
            this.previewHtml
          )});
          </${"script"}>
        </body>
        </html>
      `;
    },
    previewCodes() {
      return {
        inline: `<div rekom-inline-form form-id="${this.dynamicFormId}"></div>`,
        button: `<button onclick="rekomLoadDynamicForm('${this.dynamicFormId}')">Form button</button>`,
        link: `<a href="javascript:rekomLoadDynamicForm('${this.dynamicFormId}')">Form link</a>`,
      };
    },
    selectablePreviewVenues() {
      return (
        _.chain(this.venues)
          // All if manual, otherwise only selected venues
          .filter(
            (e) =>
              !this.dynamicForm.isAutomatic ||
              this.dynamicForm.autoInfo.venueIds.includes(e.venueId)
          )
          .map((e) => ({ text: e.name, value: e.venueId }))
          .value()
      );
    },
  },
  async created() {
    this.isLoading = true;
    this.dynamicFormId = this.$route.params.id;
    await this.$store.dispatch("dynamicForms/get", this.dynamicFormId);
    await this.$store.dispatch("guestSegmentation/get", false);
    await this.$store.dispatch("legalDocuments/getAll", false);
    await this.$store.dispatch("guestSegmentation/get");
    this.isLoading = false;
  },
};
</script>

<style lang="scss">
.top-editor-wrapper {
  border: 1px solid #999;
}

#previewPopup .vs-popup--content {
  height: 100%;
}
</style>